import { useState } from 'react';
import {
  Button, IconButton, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem,
} from '@mui/material';
import { Add, Visibility, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ExportMenu } from '../../components/ExportMenu';
import { DataTable, type Column } from '../../components/DataTable';
import { TableToolbar } from '../../components/TableToolbar';
import { StatusBadge } from '../../components/StatusBadge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PaymentDetailDialog } from './PaymentDetailDialog';
import { paymentsApi, invoicesApi, type Payment, type CreatePaymentInput } from '../../api/finance.api';
import { omsApi } from '../../api/oms.api';
import { vendorsApi } from '../../api/vendors.api';
import { clientsApi } from '../../api/clients.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const PAYMENT_TYPES = ['advance', 'partial', 'final', 'retention', 'reimbursement'];

export function PaymentListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CreatePaymentInput>({ omsId: 0, partyType: 'vendor', paymentType: 'partial', amount: 0, taxAmount: 0 });
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Payment | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['payments', page, search], queryFn: () => paymentsApi.list({ page, limit: 25, search: search || undefined }) });
  const omsOptions = useQuery({ queryKey: ['oms', 'forPay'], queryFn: () => omsApi.list({ limit: 100 }), enabled: open });
  const vendors = useQuery({ queryKey: ['vendors', 'forPay'], queryFn: () => vendorsApi.list({ limit: 100 }), enabled: open && form.partyType === 'vendor' });
  const clients = useQuery({ queryKey: ['clients', 'forPay'], queryFn: () => clientsApi.list({ limit: 100 }), enabled: open && form.partyType === 'client' });
  const invoices = useQuery({ queryKey: ['invoices', 'forPay', form.omsId, form.partyType], queryFn: () => invoicesApi.list({ limit: 100, omsId: form.omsId, partyType: form.partyType }), enabled: open && !!form.omsId });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['payments'] }); qc.invalidateQueries({ queryKey: ['finance-dashboard'] }); };

  const createMut = useMutation({
    mutationFn: () => paymentsApi.create(form),
    onSuccess: () => { setOpen(false); setForm({ omsId: 0, partyType: 'vendor', paymentType: 'partial', amount: 0, taxAmount: 0 }); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const deleteMut = useMutation({ mutationFn: (id: number) => paymentsApi.remove(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setToast(extractApiError(e)); setDeleting(null); } });

  const columns: Column<Payment>[] = [
    { key: 'code', label: 'Payment', width: 150 },
    { key: 'paymentType', label: 'Type' },
    { key: 'partyType', label: 'Party', render: (r) => `${r.partyType} #${r.vendorId ?? r.clientId}` },
    { key: 'totalAmount', label: 'Amount', align: 'right', render: (r) => `₹${r.totalAmount.toLocaleString()}` },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader title="Payments" subtitle="Request → Approval → Finance Review → Release (UTR) → Closure." action={
        <Stack direction="row" spacing={1}>
          <ExportMenu dataset="finance" permission="finance.export" filters={{ search }} />
          {hasPermission('payment.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setOpen(true); }}>New Payment</Button>}
        </Stack>
      } />
      <TableToolbar search={search} onSearch={(v) => { setSearch(v); setPage(1); }} searchPlaceholder="Search payment / UTR" />
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={25} total={data?.meta?.total ?? 0} onPageChange={setPage} onLimitChange={() => undefined} rowKey={(r) => r.id}
        emptyTitle="No payment requests yet"
        actions={(r) => (
          <>
            <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {hasPermission('payment.delete') && ['draft', 'rejected', 'cancelled'].includes(r.status) && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New Payment Request</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="OMS" value={form.omsId || ''} onChange={(e) => setForm((f) => ({ ...f, omsId: Number(e.target.value), invoiceId: null }))} fullWidth>
              {(omsOptions.data?.data ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.omsNumber} — {o.title}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField select label="Party" value={form.partyType} onChange={(e) => setForm((f) => ({ ...f, partyType: e.target.value as 'vendor' | 'client', vendorId: null, clientId: null, invoiceId: null }))} sx={{ minWidth: 130 }}>
                <MenuItem value="vendor">Vendor</MenuItem><MenuItem value="client">Client</MenuItem>
              </TextField>
              {form.partyType === 'vendor' ? (
                <TextField select label="Vendor" value={form.vendorId ?? ''} onChange={(e) => setForm((f) => ({ ...f, vendorId: Number(e.target.value) }))} fullWidth>
                  {(vendors.data?.data ?? []).map((v) => <MenuItem key={v.id} value={v.id}>{v.vendorName}</MenuItem>)}
                </TextField>
              ) : (
                <TextField select label="Client" value={form.clientId ?? ''} onChange={(e) => setForm((f) => ({ ...f, clientId: Number(e.target.value) }))} fullWidth>
                  {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
                </TextField>
              )}
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField select label="Payment Type" value={form.paymentType} onChange={(e) => setForm((f) => ({ ...f, paymentType: e.target.value }))} fullWidth>
                {PAYMENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField select label="Invoice (optional)" value={form.invoiceId ?? ''} onChange={(e) => setForm((f) => ({ ...f, invoiceId: e.target.value ? Number(e.target.value) : null }))} fullWidth>
                <MenuItem value="">— None —</MenuItem>
                {(invoices.data?.data ?? []).map((i) => <MenuItem key={i.id} value={i.id}>{i.code} (₹{i.totalAmount.toLocaleString()})</MenuItem>)}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Amount" type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: Number(e.target.value) }))} fullWidth />
              <TextField label="Tax" type="number" value={form.taxAmount} onChange={(e) => setForm((f) => ({ ...f, taxAmount: Number(e.target.value) }))} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.omsId || (form.partyType === 'vendor' ? !form.vendorId : !form.clientId) || !form.amount} onClick={() => createMut.mutate()}>Create</Button>
        </DialogActions>
      </Dialog>

      <PaymentDetailDialog paymentId={detailId} onClose={() => { setDetailId(null); invalidate(); }} />
      <ConfirmDialog open={Boolean(deleting)} title="Delete payment" message={`Delete ${deleting?.code}?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
